"use client"

import { useEffect, useRef, useState } from "react"
import Image from "next/image"
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"

import { MerchantCategory } from "@/lib/api/merchant-service"
import { cn } from "@/lib/utils"

interface CategorySliderProps {
  categories: MerchantCategory[]
}

export function CategorySlider({ categories }: CategorySliderProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [showLeftButton, setShowLeftButton] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true)

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current
      setShowLeftButton(scrollLeft > 0)
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1) // -1 for precision issues
    }
  }

  useEffect(() => {
    checkScrollButtons()
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener("scroll", checkScrollButtons)
      window.addEventListener("resize", checkScrollButtons) // Re-check on resize
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", checkScrollButtons)
        window.removeEventListener("resize", checkScrollButtons)
      }
    }
  }, [categories]) // Re-check if categories change, causing potential resize

  const handleScroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth * 0.95
      if (direction === "right") {
        scrollContainerRef.current.scrollBy({
          left: scrollAmount,
          behavior: "smooth",
        })
      } else {
        scrollContainerRef.current.scrollBy({
          left: -scrollAmount,
          behavior: "smooth",
        })
      }
      // checkScrollButtons will be called by the scroll event listener
    }
  }

  if (!categories || categories.length === 0) {
    return null
  }

  return (
    <div className="relative mb-4">
      <div
        ref={scrollContainerRef}
        className="flex space-x-4 overflow-x-auto scrollbar-hide"
        style={{ scrollSnapType: "x mandatory" }}
      >
        {categories.map((category) => (
          <div
            key={category.id}
            className="flex-shrink-0 w-20 md:w-24 text-center"
            style={{ scrollSnapAlign: "start" }}
          >
            <div className="relative mx-auto size-14 md:size-20">
              <Image
                src={category.image}
                alt={category.category}
                width={80}
                height={80}
                className="object-contain w-full h-full"
              />
            </div>
            <p className="mt-0.5 text-zinc-800 break-words text-xs md:text-sm">
              {category.category}
            </p>
          </div>
        ))}
      </div>

      {showLeftButton && (
        <div className="px-8 h-20 absolute left-0 top-0 bottom-0 bg-linear-to-l from-gray-200/0 to-gray-200/25 rounded-l-xl">
          <button
            onClick={() => handleScroll("left")}
            className={cn(
              "absolute left-0 top-10 -translate-y-1/2 z-10 p-1 bg-white rounded-full shadow-xl focus:outline-none",
              "transform translate-x-1" // Inset effect
            )}
            aria-label="Scroll left"
          >
            <ChevronLeftIcon className="h-6 w-6 text-gray-700" />
          </button>
        </div>
      )}

      {canScrollRight && (
        <div className="px-8 h-20 absolute right-0 top-0 bottom-0 bg-linear-to-r from-gray-200/0 to-gray-200/20 rounded-r-xl">
          <button
            onClick={() => handleScroll("right")}
            className={cn(
              "absolute right-0 top-10 -translate-y-1/2 z-10 p-1 bg-white rounded-full shadow-xl focus:outline-none",
              "transform -translate-x-1" // Inset effect
            )}
            aria-label="Scroll right"
            disabled={!canScrollRight}
          >
            <ChevronRightIcon className="h-6 w-6 text-gray-700" />
          </button>
        </div>
      )}
    </div>
  )
}

// Helper for hiding scrollbar, add to your global CSS if not already present
/*
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
*/
